import type { ListCopy } from 'lib/listCopy'
import { triggerContactProvision, triggerDef, triggerObject, triggerSubject } from './triggerCatalog'
import type { TargetObject, TriggerFlowNode, TriggerNodeData, WorkflowNode } from 'app/types/workflows'

/** Whether a given object (contact or activity) is in scope for the run:
 *  - `guaranteed` — every run has it (block an action only if somehow absent).
 *  - `conditional` — it may be missing at run time → warn on actions that act on it.
 *  - `none`       — never present → block actions that act on it. */
export type ObjectAvailability = `guaranteed` | `conditional` | `none`

// `date` exists so the condition editor can offer the engine's relative-date grammar (`$now-18y`)
// instead of a free-text box. It changes how a value is AUTHORED, never how it serializes.
// `boolean` fields serialize differently per destination — see BOOLEAN_FIELD_KEYS in serialize.ts.
export type FieldType = `text` | `email` | `phone` | `date` | `boolean` | `daysAgo`

/** A field available on the contact moving through the workflow — the variable namespace.
 *  Placeholder for the real contact schema (incl. custom fields) from the API. */
export type ContactField = {
  key: string
  label: string
  type: FieldType
  /** False for fields that are DERIVED rather than stored, so nothing can compare them. They are
   *  still perfectly good variables — the renderer derives them the same way the drawer does — but
   *  they are not offered as conditions, because a condition on a field that does not exist on the
   *  record silently sends every contact down the "no" path. */
  matchable?: false
  /** False for fields that can be COMPARED but not rendered: they live on a related record the
   *  engine loads only to evaluate a condition, so there is no value to substitute into a message. */
  variable?: false
}

/** The builder key for a contact's tags. See its CONTACT_FIELDS entry. */
export const CONTACT_TAGS_FIELD = `contactTags`

// Real Contact fields (keys match the app's Contact model / `src/lib/contactFieldConfig.ts` and the
// generated `Contact` GraphQL type) so {{variables}}, conditions, and SMS toField line up with the
// engine — no more invented keys like `mobilePhone`/`fullName`/`company`.
export const CONTACT_FIELDS: ContactField[] = [
  // A contact stores ONE name. First and last are split out of it for display, so there is no field
  // for the engine to compare and a condition on them never matches (measured on dev 08-14: every
  // other contact field matched its own live value, these two took the default path). They stay as
  // variables, where they render correctly, and are excluded from condition pickers. Use Full name.
  { key: `firstName`, label: `First name`, type: `text`, matchable: false },
  { key: `lastName`, label: `Last name`, type: `text`, matchable: false },
  { key: `name`, label: `Full name`, type: `text` },
  { key: `email`, label: `Email`, type: `email` },
  { key: `cellPhone`, label: `Cell phone`, type: `phone` },
  { key: `homePhone`, label: `Home phone`, type: `phone` },
  { key: `workPhone`, label: `Work phone`, type: `phone` },
  { key: `leadStatus`, label: `Lead status`, type: `text` },
  { key: `lifecycleStage`, label: `Lifecycle`, type: `text` },
  { key: `patientStatus`, label: `Patient status`, type: `text` },
  { key: `appointmentStatus`, label: `Appointment status`, type: `text` },
  // Typed so `serialize.toStepValue` sends a real boolean: step filters are compared with `===`,
  // where the string "true" admits every perio patient rather than excluding them (TRK-5734).
  { key: `isPerio`, label: `Perio patient`, type: `boolean` },
  // `pipelineStage` isn't on the Contact event the engine reads yet — it can't fire or filter on it.
  // Kept here so the builder can author pipeline-stage triggers/variables now; serialize.ts flags the
  // gap and blocks Activate until the backend adds a pipeline-stage event (TRK-5347).
  { key: `pipelineStage`, label: `Pipeline stage`, type: `text` },
  // A real persisted Contact field (multi-location practices scope contacts by location). Conditions
  // compare the id; the condition editor renders a location picker so users choose by name.
  { key: `locationId`, label: `Location`, type: `text` },
  { key: `leadOwner`, label: `Contact owner`, type: `text` },
  { key: `unbookedTreatmentValue`, label: `Unbooked treatment value`, type: `text` },
  // The engine attribute is `tags`, which the activity layer already uses as its own builder key, and
  // a contact key on an activity trigger is flagged as unmatchable. So the builder names it apart and
  // serialize renames it (CONTACT_TAGS_ATTRIBUTE). A list, so `is` means "has this tag" in both the
  // step and trigger matchers. Not a variable: nothing renders a tag list into a message.
  { key: CONTACT_TAGS_FIELD, label: `Tags`, type: `text`, variable: false },
  // The contact's date fields, the same list a date-anchored trigger counts from (see
  // TRIGGER_DATE_FIELDS — probed against the API's own catalogue, which rejects anything else by
  // name). They were absent here, so a date rule like "18 or over" could not be authored as a
  // condition at all. Comparable: `birthDate` and `createdDate` were measured against real
  // contacts on dev 2026-08-28, on both the trigger's state filters and a Filter/Branch step,
  // and `nearestAppointmentDate` on 08-28 before that.
  { key: `birthDate`, label: `Birthday`, type: `date` },
  { key: `hygieneDueDate`, label: `Hygiene due date`, type: `date` },
  { key: `lastHygieneDate`, label: `Last hygiene visit`, type: `date` },
  { key: `nearestAppointmentDate`, label: `Nearest appointment date`, type: `date` },
  { key: `lastTreatmentPlanDate`, label: `Last treatment plan date`, type: `date` },
  { key: `lastActivityDate`, label: `Last activity`, type: `date` },
  { key: `createdDate`, label: `Date created`, type: `date` },
]

export const CONTACT_FIELD_KEYS = CONTACT_FIELDS.map((field) => field.key)

// The `pipelineStage` key never changes (it is serialized), but what a user reads for it follows
// the `grouped-navigation` toggle, same shape as `permissionDisplayName` in roleFormUtils.ts. Every
// caller that RENDERS a field label (never the field catalog itself) goes through this.
export function fieldDisplayLabel(field: { key: string; label: string }, copy: ListCopy): string {
  if (field.key === `pipelineStage`) return copy.listStageField
  return field.label
}

// —— Activity = the universal BINDER; `Activity.typeId` links to the type-specific DETAIL object ——
// The binder carries the fields EVERY activity has (real Activity schema keys, so conditions/variables
// serialize to the engine unchanged). The type-specific detail (a Call's `answered`, a Form's `answers`)
// lives on the object `typeId` resolves to (Call via GraphQL, Form via its FormJson record) and is
// layered on top only when the trigger is scoped to that type. See the binder+detail audit.

/** Universal Activity binder fields — present on any activity, whatever its type. */
export const ACTIVITY_FIELDS: ContactField[] = [
  { key: `type`, label: `Activity type`, type: `text` },
  { key: `subType`, label: `Sub-type`, type: `text` },
  { key: `activityDate`, label: `Date`, type: `text` },
  { key: `contactName`, label: `Name on activity`, type: `text` },
  { key: `contactPhone`, label: `Phone on activity`, type: `phone` },
  { key: `source`, label: `Source`, type: `text` },
  { key: `vendor`, label: `Vendor`, type: `text` },
  { key: `channel`, label: `Channel`, type: `text` },
  { key: `trackingNumberName`, label: `Tracking number`, type: `text` },
  { key: `tags`, label: `Tags`, type: `text` },
]

const ACTIVITY_FIELD_KEYS = ACTIVITY_FIELDS.map((field) => field.key)

/** Call DETAIL — from the Call object (`Activity.typeId` → Call). Surfaces only when scoped to Call. */
export const CALL_DETAIL_FIELDS: ContactField[] = [
  // The processed-call outcome, and the honest way to ask "was this missed?" — `answered` is the raw
  // carrier signal and disagrees (a call can pick up and still be a Missed Call, live data 08-12).
  // On a trigger this field is one object away from the event's record, so it reads like the obvious
  // choice and matches nothing; kept because it is correct for reading a call.
  { key: `allPatientOutcomes`, label: `Call outcome`, type: `text` },
  { key: `answered`, label: `Answered (carrier signal)`, type: `boolean` },
  { key: `direction`, label: `Direction`, type: `text` },
  { key: `duration`, label: `Duration`, type: `text` },
  // The only way a missed-call reply reaches someone the practice has never met: inbound calls do
  // not create contacts (measured on dev 08-18), so "text the caller back" cannot mean "text a
  // contact field". Sendable and suppressible without one — sendSms takes contactId as optional and
  // opt-out is keyed by number.
  { key: `callerPhone`, label: `Caller's number`, type: `phone` },
]

const CALL_DETAIL_KEYS = CALL_DETAIL_FIELDS.map((field) => field.key)

/** Form DETAIL — the standard fields every FormJson carries (`Activity.typeId` → Form). A specific
 *  form's custom fields (`answers.*`) are resolved live from a submission and layered on in the scope
 *  step; these are the ones present on every form. */
export const FORM_DETAIL_FIELDS: ContactField[] = [
  { key: `title`, label: `Form title`, type: `text` },
  { key: `patientName`, label: `Name`, type: `text` },
  // The form MAPPING's output: ingestion extracts the submitted phone from the vendor's answer
  // fields into this envelope field, so "the phone the form submitted" and "what the mapping found"
  // are the same thing. As a Send-SMS target it is the form counterpart of callerPhone: the number
  // the lead just typed beats a stale cellPhone.
  { key: `phone`, label: `Phone the form submitted`, type: `phone` },
  { key: `treatment`, label: `Treatment`, type: `text` },
  { key: `requestedAppointmentDate`, label: `Requested date`, type: `text` },
]

const FORM_DETAIL_KEYS = FORM_DETAIL_FIELDS.map((field) => field.key)

/** Fields on the triggering Message object (the inbox conversation object — SMS/email). Keys are the
 *  frontend-contracted event attributes (globally unique: `direction`/`channel` are taken by Call/
 *  Activity), to be implemented by the engine's message-created event (see TRK-5354 message sub-task).
 *  `fromNumber`/`toNumber` are phone-typed, so e.g. Send SMS can target "the number that texted us". */
export const MESSAGE_FIELDS: ContactField[] = [
  { key: `messageDirection`, label: `Direction`, type: `text` },
  { key: `messageChannel`, label: `Channel`, type: `text` },
  { key: `messageText`, label: `Message text`, type: `text` },
  { key: `fromNumber`, label: `From number`, type: `phone` },
  { key: `toNumber`, label: `To number`, type: `phone` },
]

const MESSAGE_FIELD_KEYS = MESSAGE_FIELDS.map((field) => field.key)

/** Fields on the triggering Appointment object (its own model — status, provider, date). */
export const APPOINTMENT_FIELDS: ContactField[] = [
  { key: `status`, label: `Status`, type: `text` },
  { key: `treatmentCategories`, label: `Procedure category`, type: `text` },
  { key: `providerId`, label: `Provider`, type: `text` },
  { key: `date`, label: `Date`, type: `text` },
  // What the visit was for, and the difference between a recall ladder that fires on hygiene and one
  // that fires on a crown seat. The vocabulary is the practice's own, straight from the PMS: on dev
  // it reads "New Patient - Exam & Cleaning" (200), "Consultation" (78), "New Patient - Emergency
  // Exam" (34), "Existing Patient - Exam & Cleaning" (21), "Prophy-Child" (4). So a hygiene check is
  // a `contains` on a word like "Cleaning", never an equals against a fixed list we invent here.
  { key: `type`, label: `Appointment type`, type: `text` },
  // Asking by CDT code works today; asking by CATEGORY does not — `CdtCodeMapping` holds zero rows,
  // so "codes are Preventative" has no stored definition to resolve against (TRK-5548).
  { key: `procedureCodes`, label: `Procedure codes`, type: `text` },
  // Whether the patient confirmed, read from Kolla on every sync (TRK-6045). Null until the first
  // sync and for every other vendor, so "is not yes" is the question that includes those.
  { key: `confirmed`, label: `Confirmed`, type: `boolean`, variable: false },
  // Rendered by the engine from the appointment the run enrolled from (TRK-6077), under the
  // `appointment.*` namespace in APPOINTMENT_TOKENS. Not stored, so nothing can compare them.
  { key: `appointmentDay`, label: `Appointment day`, type: `text`, matchable: false },
  { key: `appointmentTime`, label: `Appointment time`, type: `text`, matchable: false },
  { key: `providerName`, label: `Provider name`, type: `text`, matchable: false },
  // The patient, who on a shared family phone is often not the contact being texted.
  { key: `patientFirstName`, label: `Patient first name`, type: `text`, matchable: false },
  { key: `locationName`, label: `Practice name`, type: `text`, matchable: false },
]

const APPOINTMENT_FIELD_KEYS = APPOINTMENT_FIELDS.map((field) => field.key)

const APPOINTMENT_TOKENS: Readonly<Record<string, string>> = {
  appointmentDay: `appointment.date`,
  appointmentTime: `appointment.time`,
  providerName: `appointment.providerName`,
  patientFirstName: `appointment.patientFirstName`,
  locationName: `appointment.locationName`,
}

/** Fields on the triggering Task object — the real model fields task events record (verified 08-06:
 *  event data carries title/status/dueDate/description/ownerId; status is open|closed). */
export const TASK_FIELDS: ContactField[] = [
  { key: `title`, label: `Task title`, type: `text` },
  { key: `status`, label: `Task status`, type: `text` },
  { key: `dueDate`, label: `Due date`, type: `text` },
  { key: `description`, label: `Task description`, type: `text` },
  { key: `ownerId`, label: `Task owner`, type: `text` },
]

const TASK_FIELD_KEYS = TASK_FIELDS.map((field) => field.key)

/** The triggering treatment plan record. Its name is free text, so name checks use "contains". */
const TREATMENT_PLAN_FIELDS: ContactField[] = [
  { key: `displayName`, label: `Plan name`, type: `text` },
  { key: `state`, label: `Plan state`, type: `text` },
]

const TREATMENT_PLAN_FIELD_KEYS = TREATMENT_PLAN_FIELDS.map((field) => field.key)

/** What a treatment plan event says just changed. They live on the event, not the plan record,
 *  so only the trigger's change rows can read them. */
const TREATMENT_EVENT_FIELDS: ContactField[] = [
  { key: `changedValue`, label: `Value of the changed treatment`, type: `text`, variable: false },
  { key: `pendingLinesAfter`, label: `Treatment still pending`, type: `text`, variable: false },
  { key: `unbookedValueAfter`, label: `Unbooked value after the change`, type: `text`, variable: false },
  { key: `appointmentDate`, label: `Appointment date`, type: `date`, variable: false },
  { key: `providerId`, label: `Provider`, type: `text`, variable: false },
]

const TREATMENT_EVENT_FIELD_KEYS = TREATMENT_EVENT_FIELDS.map((field) => field.key)

/** Fields on the triggering PMS comm log, as the comm_log_created event carries them. Direction is
 *  absent on many live records, so "unknown" is the `is empty` operator rather than a third value.
 *  The channel label is a variable only: the stored channel is what conditions compare. */
const COMM_LOG_FIELDS: ContactField[] = [
  { key: `commLogChannel`, label: `Channel`, type: `text` },
  { key: `sentOrReceived`, label: `Direction`, type: `text` },
  { key: `commLogSubject`, label: `Subject`, type: `text` },
  { key: `commLogNote`, label: `Note`, type: `text` },
  { key: `commDate`, label: `When it happened`, type: `date` },
  { key: `commLogChannelLabel`, label: `Channel name`, type: `text`, matchable: false },
]

const COMM_LOG_FIELD_KEYS = COMM_LOG_FIELDS.map((field) => field.key)

/** Fields on the contact's SMS thread. Not part of the record moving through the workflow: the
 *  engine looks the thread up from the contact's phone when a condition asks for it (branch.ts,
 *  api #684). Available wherever a contact is, and evaluated against an empty record when the
 *  contact has no phone or has never been texted, so "engaged" reads false rather than erroring.
 *  Conditions only. A thread field is not a variable, because nothing renders one into a message. */
export const THREAD_FIELDS: ContactField[] = [
  { key: `aiEnabled`, label: `AI agent engaged`, type: `boolean`, variable: false },
  { key: `agentLastMessageDate`, label: `Agent last messaged`, type: `date`, variable: false },
  // Their own type, not `date`: the generic date editor reads backwards here ("less than 1 day
  // before today" means earlier). These author as "more/less than N days ago" instead.
  { key: `lastIncomingMessageDate`, label: `Last inbound text`, type: `daysAgo`, variable: false },
  { key: `lastOutgoingMessageDate`, label: `Last outbound text`, type: `daysAgo`, variable: false },
]

export const THREAD_FIELD_KEYS = THREAD_FIELDS.map((field) => field.key)

export const PAPERWORK_LINK_FIELD = `paperwork.link`
export const PAPERWORK_STATUS_FIELD = `paperworkStatus`

const PAPERWORK_FIELDS: ContactField[] = [
  { key: PAPERWORK_LINK_FIELD, label: `Paperwork link`, type: `text`, matchable: false },
  { key: PAPERWORK_STATUS_FIELD, label: `Paperwork status`, type: `text`, variable: false },
]

const FIELD_LAYERS: readonly { source: FieldSource; fields: ContactField[] }[] = [
  { source: `contact`, fields: CONTACT_FIELDS },
  { source: `activity`, fields: ACTIVITY_FIELDS },
  { source: `call`, fields: CALL_DETAIL_FIELDS },
  { source: `form`, fields: FORM_DETAIL_FIELDS },
  { source: `message`, fields: MESSAGE_FIELDS },
  { source: `appointment`, fields: APPOINTMENT_FIELDS },
  { source: `task`, fields: TASK_FIELDS },
  { source: `treatmentPlan`, fields: [...TREATMENT_PLAN_FIELDS, ...TREATMENT_EVENT_FIELDS] },
  { source: `commLog`, fields: COMM_LOG_FIELDS },
  { source: `thread`, fields: THREAD_FIELDS },
  { source: `paperwork`, fields: PAPERWORK_FIELDS },
]

/** Every field the variable/phone helpers can resolve, across all layers. */
const ALL_FIELDS: ContactField[] = FIELD_LAYERS.flatMap((layer) => layer.fields)

const SOURCE_BY_FIELD = new Map(
  FIELD_LAYERS.flatMap((layer) => layer.fields.map((field) => [field, layer.source] as const)),
)

const SHARED_KEYS = new Set(
  ALL_FIELDS.map((field) => field.key).filter((key, index, keys) => keys.indexOf(key) !== index),
)

/** A few keys name a different field on different records (`type` is an activity's and an
 *  appointment's). No trigger provides both, so a shared key resolves to the layer that has one of
 *  its own unshared keys in scope. */
function resolveField(key: string, scope: string[]): ContactField | undefined {
  if (!SHARED_KEYS.has(key)) return ALL_FIELDS.find((field) => field.key === key)
  const layer = FIELD_LAYERS.find(
    (candidate) =>
      candidate.fields.some((field) => field.key === key) &&
      candidate.fields.some((field) => !SHARED_KEYS.has(field.key) && scope.includes(field.key)),
  )
  return (layer?.fields ?? ALL_FIELDS).find((field) => field.key === key)
}

/** The type-specific DETAIL fields layered onto the Activity binder for an activity-sourced trigger:
 *  Call → the Call object's fields; a Form-scoped Activity → the standard FormJson fields plus the
 *  selected form(s)' own fields, surfaced as `answers.<name>` (resolved live in the Scope step). */
function detailFieldsForTrigger(data: TriggerNodeData): string[] {
  const object = triggerObject(data.kind)
  if (object === `call`) return CALL_DETAIL_KEYS
  if (object === `form` || data.activityType === `Form`) {
    const answerFields = (data.formFields ?? []).map((name) => `answers.${name}`)
    return [...FORM_DETAIL_KEYS, ...answerFields]
  }
  return []
}

/** Which fields a given trigger reliably provides downstream, composed as binder ∪ detail ∪ contact:
 *  - Appointment / Task events → the object's own fields plus the contact they resolve to.
 *  - Activity / Call events → the Activity BINDER fields + the type-specific DETAIL fields; the contact
 *    record is added only when the trigger always resolves to one (or the user requires a matched one).
 *  - Contact events → the contact record. */
export function availableFieldsForTrigger(data: TriggerNodeData): string[] {
  const def = triggerDef(data.kind)
  const object = triggerObject(data.kind)
  if (object === `appointment`) return [...APPOINTMENT_FIELD_KEYS, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS]
  if (object === `task`) return [...TASK_FIELD_KEYS, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS]
  if (object === `treatment_plan`) return [...TREATMENT_PLAN_FIELD_KEYS, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS]
  if (object === `message`) {
    // A message doesn't always resolve to a contact (unknown inbound number) — same contract as calls.
    const hasContact = def.contactProvision === `always` || data.requireContactMatch
    return hasContact ? [...MESSAGE_FIELD_KEYS, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS] : MESSAGE_FIELD_KEYS
  }
  if (object === `comm_log`) {
    const hasContact = def.contactProvision === `always` || data.requireContactMatch
    return hasContact ? [...COMM_LOG_FIELD_KEYS, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS] : COMM_LOG_FIELD_KEYS
  }
  if (triggerSubject(data.kind) === `activity`) {
    const base = [...ACTIVITY_FIELD_KEYS, ...detailFieldsForTrigger(data)]
    const hasContact = def.contactProvision === `always` || data.requireContactMatch
    return hasContact ? [...base, ...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS] : base
  }
  return [...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS]
}

/** The fields a trigger's own conditions can match. Both condition groups read the triggering record
 *  (its event, or the live record), never the contact it resolves to, and never the SMS thread. A
 *  field a saved condition already uses stays listed so that row still reads back. */
export function triggerConditionFields(data: TriggerNodeData): string[] {
  const onContact = triggerObject(data.kind) === `contact`
  const own = availableFieldsForTrigger(data).filter(
    (key) => !THREAD_FIELD_KEYS.includes(key) && (onContact || !CONTACT_FIELD_KEYS.includes(key)),
  )
  const used = (data.conditions ?? []).flatMap((condition) =>
    condition.type === `field` && condition.field ? [condition.field] : [],
  )
  return [...new Set([...own, ...used])]
}

/** A treatment plan trigger reads two records: its change rows read the event, its check rows read
 *  the plan. Every other trigger reads one record and offers both groups the same fields. */
export function triggerConditionFieldsByScope(data: TriggerNodeData): { change: string[]; state: string[] } {
  const fields = triggerConditionFields(data)
  if (triggerObject(data.kind) !== `treatment_plan`) return { change: fields, state: fields }
  return {
    change: [
      ...new Set([...TREATMENT_EVENT_FIELD_KEYS, ...fields.filter((key) => !TREATMENT_PLAN_FIELD_KEYS.includes(key))]),
    ],
    state: fields,
  }
}

function isTriggerNode(node: WorkflowNode): node is TriggerFlowNode {
  return node.type === `trigger`
}

/** The fields available to every step, derived from the workflow's trigger. */
export function deriveAvailableFields(nodes: WorkflowNode[]): string[] {
  const trigger = nodes.find(isTriggerNode)
  const fields = trigger ? availableFieldsForTrigger(trigger.data) : [...CONTACT_FIELD_KEYS, ...THREAD_FIELD_KEYS]
  const hasPaperwork =
    trigger?.data.kind === `paperwork_completed` || nodes.some((node) => node.type === `sendPaperwork`)
  return hasPaperwork ? [...fields, PAPERWORK_STATUS_FIELD] : fields
}

/** Whether a contact is in scope for the workflow, from the trigger event + its enrollment options.
 *  Drives contact-targeting validation. `guaranteed` when there's no trigger yet (the workflow-level
 *  "needs a trigger" check handles that case). An `activity` event is `conditional` unless the
 *  trigger requires a matched contact, which upgrades it to `guaranteed`. */
export function deriveContactAvailability(nodes: WorkflowNode[]): ObjectAvailability {
  const trigger = nodes.find(isTriggerNode)
  if (!trigger) return `guaranteed`
  const provision = triggerContactProvision(trigger.data.kind)
  if (provision === `always`) return `guaranteed`
  return trigger.data.requireContactMatch ? `guaranteed` : `conditional`
}

/** Whether the triggering Activity is in scope — `guaranteed` for any activity-sourced trigger
 *  (the activity IS the subject), `none` otherwise (a contact trigger has no activity to act on). */
export function deriveActivityAvailability(nodes: WorkflowNode[]): ObjectAvailability {
  const trigger = nodes.find(isTriggerNode)
  if (!trigger) return `none`
  return triggerSubject(trigger.data.kind) === `activity` ? `guaranteed` : `none`
}

/** Availability of a specific target object, from the trigger. */
export function objectAvailability(target: TargetObject, nodes: WorkflowNode[]): ObjectAvailability {
  return target === `activity` ? deriveActivityAvailability(nodes) : deriveContactAvailability(nodes)
}

/** The target objects an "object" action can choose from — those actually in scope for the trigger. */
export function availableTargets(contact: ObjectAvailability, activity: ObjectAvailability): TargetObject[] {
  const targets: TargetObject[] = []
  if (contact !== `none`) targets.push(`contact`)
  if (activity !== `none`) targets.push(`activity`)
  return targets
}

/** A selected form's own field, addressed as `answers.<name>`; the label is the field name itself. */
const ANSWER_PREFIX = `answers.`
function answerFieldFor(key: string): ContactField | undefined {
  return key.startsWith(ANSWER_PREFIX) ? { key, label: key.slice(ANSWER_PREFIX.length), type: `text` } : undefined
}

/** A field key → the token the engine actually resolves. Confirmed with Ryan on TRK-5498 and
 *  matched against renderNodeText: contact fields are namespaced `contact.*`, fields from the record
 *  that triggered the run are `activity.*`, a form's answers are `form.answer.<name>`, and globals
 *  like currentDateTime stay flat. Bare `{{firstName}}` renders EMPTY, so this mapping is the
 *  difference between a personalised message and a blank one. */
export function engineVariableToken(key: string): string {
  if (key.startsWith(ANSWER_PREFIX)) return `form.answer.${key.slice(ANSWER_PREFIX.length)}`
  if (key === PAPERWORK_LINK_FIELD) return key
  if (CONTACT_FIELD_KEYS.includes(key)) return `contact.${key}`
  return APPOINTMENT_TOKENS[key] ?? `activity.${key}`
}

/** Reverse of the above, so the builder's preview can look a token up in a sample keyed by plain
 *  field name. */
export function fieldKeyFromToken(token: string): string {
  if (token.startsWith(`form.answer.`)) return `${ANSWER_PREFIX}${token.slice(`form.answer.`.length)}`
  if (token.startsWith(`contact.`)) return token.slice(`contact.`.length)
  if (token.startsWith(`activity.`)) return token.slice(`activity.`.length)
  return Object.keys(APPOINTMENT_TOKENS).find((key) => APPOINTMENT_TOKENS[key] === token) ?? token
}

/** Human label for an ENGINE token ("contact.firstName" → "First name"). Unknown tokens keep their
 *  last path part so hand-typed ones still read as words rather than plumbing. Goes through
 *  `fieldDisplayLabel` so a `pipelineStage` token reads "List stage" once the lists are on. */
export function labelForToken(token: string, copy: ListCopy): string {
  const key = fieldKeyFromToken(token)
  const label = fieldLabelByKey(key) ?? fieldLabelByKey(token) ?? token.split(`.`).pop() ?? token
  return fieldDisplayLabel({ key, label }, copy)
}

/** Every catalog field the trigger puts in scope, variables and condition-only alike. */
function fieldsInScope(availableFields: string[]): ContactField[] {
  // Preserve availableFields order (binder → detail → form answers → contact) and include the dynamic
  // `answers.*` form fields, which aren't in the static catalog.
  return availableFields
    .map((key) => answerFieldFor(key) ?? resolveField(key, availableFields))
    .filter((field): field is ContactField => !!field)
}

export function fieldsToVariables(availableFields: string[]): ContactField[] {
  return fieldsInScope(availableFields).filter((field) => field.variable !== false)
}

export function fieldLabels(availableFields: string[]): string[] {
  return fieldsToVariables(availableFields).map((field) => field.label)
}

/** Human label for a single field key (across every layer, incl. dynamic form `answers.*` fields).
 *  Pass the trigger's available fields to name a shared key after the record in scope. */
export function fieldLabelByKey(key?: string, scope: string[] = []): string | undefined {
  if (!key) return undefined
  if (key.startsWith(ANSWER_PREFIX)) return key.slice(ANSWER_PREFIX.length)
  return resolveField(key, scope)?.label
}

/** Whether a field holds a date, so a condition on it is authored with the relative-date control
 *  rather than a free-text box. Unknown keys (a form's `answers.*`) are not dates. */
export function isDateFieldKey(key?: string): boolean {
  return !!key && ALL_FIELDS.find((field) => field.key === key)?.type === `date`
}

export function isDaysAgoFieldKey(key?: string): boolean {
  return !!key && ALL_FIELDS.find((field) => field.key === key)?.type === `daysAgo`
}

/** Whether a field holds a true/false value, so a condition on it offers Yes/No rather than a text
 *  box, and so the serializer knows to send a real boolean where the destination compares by
 *  identity. */
export function isBooleanFieldKey(key?: string): boolean {
  return !!key && ALL_FIELDS.find((field) => field.key === key)?.type === `boolean`
}

/** Phone-type fields available in the current context — the numbers a Send SMS can text (a contact
 *  phone, or the number an activity came in on). */
export function availablePhoneFields(availableFields: string[]): ContactField[] {
  return ALL_FIELDS.filter((field) => field.type === `phone` && availableFields.includes(field.key))
}

/** Which layer a field belongs to — so the UI can always say where a field comes from. `activity` is
 *  the universal binder; `call` / `form` are the type-specific detail layers on top of it. */
export type FieldSource =
  | `contact`
  | `activity`
  | `call`
  | `form`
  | `message`
  | `appointment`
  | `task`
  | `treatmentPlan`
  | `commLog`
  | `thread`
  | `paperwork`
const SOURCE_LABEL: Record<FieldSource, string> = {
  contact: `Contact`,
  activity: `Activity`,
  call: `Call`,
  form: `Form`,
  message: `Message`,
  appointment: `Appointment`,
  task: `Task`,
  treatmentPlan: `Treatment plan`,
  commLog: `PMS comm log`,
  thread: `SMS thread`,
  paperwork: `Paperwork`,
}

export function fieldSource(key: string): FieldSource {
  if (key.startsWith(ANSWER_PREFIX)) return `form`
  if (ACTIVITY_FIELDS.some((field) => field.key === key)) return `activity`
  if (CALL_DETAIL_FIELDS.some((field) => field.key === key)) return `call`
  if (FORM_DETAIL_FIELDS.some((field) => field.key === key)) return `form`
  if (MESSAGE_FIELDS.some((field) => field.key === key)) return `message`
  if (APPOINTMENT_FIELDS.some((field) => field.key === key)) return `appointment`
  if (TASK_FIELDS.some((field) => field.key === key)) return `task`
  if (
    TREATMENT_PLAN_FIELDS.some((field) => field.key === key) ||
    TREATMENT_EVENT_FIELDS.some((field) => field.key === key)
  )
    return `treatmentPlan`
  if (COMM_LOG_FIELDS.some((field) => field.key === key)) return `commLog`
  if (THREAD_FIELDS.some((field) => field.key === key)) return `thread`
  if (PAPERWORK_FIELDS.some((field) => field.key === key)) return `paperwork`
  return `contact`
}

export type FieldGroup = { source: FieldSource; label: string; fields: ContactField[] }

/** Group fields by their layer, in that order, omitting empty groups. */
export function groupFieldsBySource(fields: ContactField[]): FieldGroup[] {
  const order: FieldSource[] = [
    `contact`,
    `activity`,
    `call`,
    `form`,
    `message`,
    `appointment`,
    `treatmentPlan`,
    `commLog`,
    `task`,
    `thread`,
    `paperwork`,
  ]
  return order
    .map((source) => ({
      source,
      label: SOURCE_LABEL[source],
      fields: fields.filter((field) => (SOURCE_BY_FIELD.get(field) ?? fieldSource(field.key)) === source),
    }))
    .filter((group) => group.fields.length > 0)
}

/** Available fields grouped by source — drives the labeled sections in pickers and chips. */
export function fieldsBySource(availableFields: string[]): FieldGroup[] {
  return groupFieldsBySource(fieldsToVariables(availableFields))
}

/** The same, minus fields nothing can compare (see `matchable`). Conditions use this; the variable
 *  list uses the full one, because a derived field is a fine variable and a useless filter. */
export function conditionFieldsBySource(availableFields: string[]): FieldGroup[] {
  return groupFieldsBySource(fieldsInScope(availableFields).filter((field) => field.matchable !== false))
}

/** The fields a test submission can carry for a trigger — its type-specific DETAIL fields (Form /
 *  Call), so the "compose a test submission" UI offers exactly the data the workflow can use. Contact
 *  and generic-activity binder fields are excluded (those come from the contact / are auto-populated). */
export function composableTestFields(data: TriggerNodeData): { key: string; label: string }[] {
  return fieldsToVariables(availableFieldsForTrigger(data))
    .filter((field) => fieldSource(field.key) === `form` || fieldSource(field.key) === `call`)
    .map((field) => ({ key: field.key, label: field.label }))
}

/** Date fields a date-anchored trigger can count from, per source object.
 *
 *  Probed against the API's own catalog on 08-19 rather than guessed: it rejects anything else by
 *  name ("Date field \"nextAppointmentDate\" is not supported for Contact triggers"), so this list
 *  is the real one. `appointmentDateTime` combines the appointment's date and time, which is why an
 *  offset of "-1d" lands at the same clock time the day before rather than at midnight.
 */
const TRIGGER_DATE_FIELDS: Record<`appointment` | `contact`, ContactField[]> = {
  appointment: [
    { key: `appointmentDateTime`, label: `Appointment date and time`, type: `text` },
    { key: `date`, label: `Appointment date`, type: `text` },
    { key: `appointmentMadeDate`, label: `Date the appointment was booked`, type: `text` },
  ],
  contact: [
    // Stored and indexed since trackable-api #525 (TRK-5554), computed as the last visit plus the
    // patient's own recall interval. Verified on dev 08-19: perio patients get 120 days and everyone
    // else 181, which is the split our UI used to do in the browser and no longer should.
    { key: `hygieneDueDate`, label: `Hygiene due date`, type: `text` },
    { key: `nearestAppointmentDate`, label: `Nearest appointment date`, type: `text` },
    { key: `lastHygieneDate`, label: `Last hygiene visit`, type: `text` },
    { key: `birthDate`, label: `Birthday`, type: `text` },
    { key: `lastActivityDate`, label: `Last activity`, type: `text` },
    { key: `createdDate`, label: `Date created`, type: `text` },
    { key: `lastTreatmentPlanDate`, label: `Last treatment plan date`, type: `text` },
  ],
}

/** Anchors that carry a calendar date but no meaningful time of day. The engine rejects a zero
 *  offset, so "on the day" is authored as "+1 hour after" (past-midnight, then the send window
 *  holds it until morning) — and every surface that SHOWS an offset should translate that back to
 *  "on the day" for these anchors instead of leaking the encoding. Appointment anchors and the
 *  activity timestamps have real times, so their offsets mean exactly what they say. */
const DATE_ONLY_ANCHORS = new Set([
  `hygieneDueDate`,
  `lastHygieneDate`,
  `birthDate`,
  // Treatment plan dates arrive from the PMS at midnight, so this is a calendar
  // date like the three above. Without it, somebody who picks "on the day" is
  // told back that their workflow runs "1 hour after".
  `lastTreatmentPlanDate`,
])

export const isDateOnlyAnchor = (dateField?: string): boolean => !!dateField && DATE_ONLY_ANCHORS.has(dateField)

/** Label for a date-trigger anchor ("hygieneDueDate" → "Hygiene due date"). The anchors live in
 *  their own catalog above, not in ALL_FIELDS, so fieldLabelByKey alone can't name them. */
export function dateAnchorLabel(dateField?: string): string | undefined {
  if (!dateField) return undefined
  return [...TRIGGER_DATE_FIELDS.appointment, ...TRIGGER_DATE_FIELDS.contact].find((field) => field.key === dateField)
    ?.label
}
